/**
 * Garment-type templates (build-plan Inc 1, M2; expanded in #124).
 *
 * Each garment type seeds the ratio grid with a sensible default size set so
 * the operator isn't typing size headers from scratch — they paste/adjust
 * quantities under pre-filled colour x size columns. The `key` is what
 * `garment_styles.garment_type` stores; the `sizes` are defaults the composer
 * offers (the operator can still add/remove sizes per style).
 *
 * The catalog is organised by `GarmentCategory` for the searchable picker.
 * The original eight V1 types are preserved at their positions.
 */

// ── Categories ────────────────────────────────────────────────────

export type GarmentCategory =
  | "knit_tops"
  | "woven_tops"
  | "bottoms"
  | "dresses_onepiece"
  | "outerwear"
  | "knitwear"
  | "activewear"
  | "inner_loungewear"
  | "ethnicwear"
  | "kidswear"
  | "accessories";

/** Ordered list for picker display. */
export const GARMENT_CATEGORIES: Array<{ key: GarmentCategory; label: string }> = [
  { key: "knit_tops", label: "Knit tops" },
  { key: "woven_tops", label: "Woven tops" },
  { key: "bottoms", label: "Bottoms" },
  { key: "dresses_onepiece", label: "Dresses & one-piece" },
  { key: "outerwear", label: "Outerwear" },
  { key: "knitwear", label: "Knitwear" },
  { key: "activewear", label: "Activewear" },
  { key: "inner_loungewear", label: "Inner & loungewear" },
  { key: "ethnicwear", label: "Ethnicwear (India)" },
  { key: "kidswear", label: "Kidswear" },
  { key: "accessories", label: "Accessories" },
];

// ── Types & templates ─────────────────────────────────────────────

export const GARMENT_TYPES = [
  // ── Knit tops ──
  "knit_tee",
  "polo",
  "henley",
  "tank_top",
  "crop_top",
  "sweatshirt",
  "rugby_shirt",
  // ── Woven tops ──
  "woven_shirt",
  "oxford_shirt",
  "flannel_shirt",
  "blouse",
  "tunic",
  // ── Bottoms ──
  "trouser",
  "jeans",
  "shorts",
  "cargo_pants",
  "joggers",
  "track_pants",
  "leggings",
  "skirt",
  "culottes",
  // ── Dresses & one-piece ──
  "dress",
  "jumpsuit",
  "romper",
  "gown",
  "playsuit",
  // ── Outerwear ──
  "hoodie",
  "jacket",
  "blazer",
  "coat",
  "parka",
  "bomber",
  "denim_jacket",
  "windcheater",
  "gilet_vest",
  // ── Knitwear ──
  "sweater",
  "cardigan",
  "pullover",
  // ── Activewear ──
  "sports_bra",
  "active_tee",
  "cycling_shorts",
  "tracksuit",
  // ── Inner & loungewear ──
  "boxer",
  "brief",
  "camisole",
  "pyjama_set",
  "nightwear",
  "robe",
  "thermal_set",
  // ── Ethnicwear (India) ──
  "kurta",
  "kurti",
  "salwar",
  "churidar",
  "saree_blouse",
  "lehenga",
  "sherwani",
  "nehru_jacket",
  // ── Kidswear ──
  "kidswear",
  "kids_romper",
  "frock",
  // ── Accessories ──
  "cap",
  "beanie",
  "scarf",
  "socks",
  "apron",
  "tote_bag",
] as const;

export type GarmentType = (typeof GARMENT_TYPES)[number];

export interface GarmentTypeTemplate {
  key: GarmentType;
  label: string;
  category: GarmentCategory;
  /** Default size columns offered for this garment type's ratio grid. */
  sizes: readonly string[];
  /** Prefix for a suggested style code (e.g. "TEE-4821"). Style codes are
   *  usually buyer-owned — this is only ever a starting suggestion. */
  styleCodePrefix: string;
}

const SZ_ALPHA = ["S", "M", "L", "XL", "XXL"] as const;
const SZ_ALPHA_XS = ["XS", "S", "M", "L", "XL"] as const;
const SZ_WOVEN = ["38", "40", "42", "44", "46"] as const;
const SZ_BOTTOM = ["30", "32", "34", "36", "38"] as const;
const SZ_KIDS = ["2-3Y", "4-5Y", "6-7Y", "8-9Y", "10-11Y"] as const;
const SZ_ONESIZE = ["Free"] as const;
const SZ_SHOE = ["6", "7", "8", "9", "10", "11"] as const;

export const GARMENT_TYPE_TEMPLATES: readonly GarmentTypeTemplate[] = [
  // ── Knit tops ────────────────────────────────────────────────────
  { key: "knit_tee",    label: "Knit T-shirt",          category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "TEE" },
  { key: "polo",        label: "Polo shirt",             category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "POLO" },
  { key: "henley",      label: "Henley",                 category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "HNLY" },
  { key: "tank_top",    label: "Tank top / vest",        category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "TANK" },
  { key: "crop_top",    label: "Crop top",               category: "knit_tops",   sizes: SZ_ALPHA_XS, styleCodePrefix: "CROP" },
  { key: "sweatshirt",  label: "Sweatshirt",             category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "SWSH" },
  { key: "rugby_shirt", label: "Rugby shirt",            category: "knit_tops",   sizes: SZ_ALPHA,    styleCodePrefix: "RGBY" },

  // ── Woven tops ───────────────────────────────────────────────────
  { key: "woven_shirt",  label: "Woven shirt",           category: "woven_tops",  sizes: SZ_WOVEN,    styleCodePrefix: "SHIRT" },
  { key: "oxford_shirt", label: "Oxford shirt",          category: "woven_tops",  sizes: SZ_WOVEN,    styleCodePrefix: "OXFD" },
  { key: "flannel_shirt",label: "Flannel shirt",         category: "woven_tops",  sizes: SZ_WOVEN,    styleCodePrefix: "FLNL" },
  { key: "blouse",       label: "Blouse",                category: "woven_tops",  sizes: SZ_ALPHA_XS, styleCodePrefix: "BLSE" },
  { key: "tunic",        label: "Tunic",                 category: "woven_tops",  sizes: SZ_ALPHA_XS, styleCodePrefix: "TNCK" },

  // ── Bottoms ──────────────────────────────────────────────────────
  { key: "trouser",      label: "Trouser / chino",       category: "bottoms",     sizes: SZ_BOTTOM,   styleCodePrefix: "TRSR" },
  { key: "jeans",        label: "Jeans / denim",         category: "bottoms",     sizes: SZ_BOTTOM,   styleCodePrefix: "JEAN" },
  { key: "shorts",       label: "Shorts",                category: "bottoms",     sizes: SZ_ALPHA,    styleCodePrefix: "SHRT" },
  { key: "cargo_pants",  label: "Cargo pants",           category: "bottoms",     sizes: SZ_BOTTOM,   styleCodePrefix: "CRGO" },
  { key: "joggers",      label: "Joggers",               category: "bottoms",     sizes: SZ_ALPHA,    styleCodePrefix: "JOGR" },
  { key: "track_pants",  label: "Track pants",           category: "bottoms",     sizes: SZ_ALPHA,    styleCodePrefix: "TRCK" },
  { key: "leggings",     label: "Leggings",              category: "bottoms",     sizes: SZ_ALPHA_XS, styleCodePrefix: "LEGG" },
  { key: "skirt",        label: "Skirt",                 category: "bottoms",     sizes: SZ_ALPHA_XS, styleCodePrefix: "SKRT" },
  { key: "culottes",     label: "Culottes",              category: "bottoms",     sizes: SZ_ALPHA_XS, styleCodePrefix: "CLTS" },

  // ── Dresses & one-piece ──────────────────────────────────────────
  { key: "dress",        label: "Dress",                 category: "dresses_onepiece", sizes: SZ_ALPHA_XS, styleCodePrefix: "DRESS" },
  { key: "jumpsuit",     label: "Jumpsuit",              category: "dresses_onepiece", sizes: SZ_ALPHA_XS, styleCodePrefix: "JMPS" },
  { key: "romper",       label: "Romper",                category: "dresses_onepiece", sizes: SZ_ALPHA_XS, styleCodePrefix: "RMPR" },
  { key: "gown",         label: "Gown",                  category: "dresses_onepiece", sizes: SZ_ALPHA_XS, styleCodePrefix: "GOWN" },
  { key: "playsuit",     label: "Playsuit",              category: "dresses_onepiece", sizes: SZ_ALPHA_XS, styleCodePrefix: "PLST" },

  // ── Outerwear ────────────────────────────────────────────────────
  { key: "hoodie",       label: "Hoodie / sweatshirt",   category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "HOOD" },
  { key: "jacket",       label: "Jacket",                category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "JKT" },
  { key: "blazer",       label: "Blazer",                category: "outerwear",   sizes: SZ_WOVEN,    styleCodePrefix: "BLZR" },
  { key: "coat",         label: "Coat",                  category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "COAT" },
  { key: "parka",        label: "Parka",                 category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "PRKA" },
  { key: "bomber",       label: "Bomber jacket",         category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "BMBR" },
  { key: "denim_jacket", label: "Denim jacket",          category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "DNJK" },
  { key: "windcheater",  label: "Windcheater",           category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "WNDC" },
  { key: "gilet_vest",   label: "Gilet / vest",          category: "outerwear",   sizes: SZ_ALPHA,    styleCodePrefix: "GILT" },

  // ── Knitwear ─────────────────────────────────────────────────────
  { key: "sweater",      label: "Sweater",               category: "knitwear",    sizes: SZ_ALPHA,    styleCodePrefix: "SWTR" },
  { key: "cardigan",     label: "Cardigan",              category: "knitwear",    sizes: SZ_ALPHA,    styleCodePrefix: "CRDG" },
  { key: "pullover",     label: "Pullover",              category: "knitwear",    sizes: SZ_ALPHA,    styleCodePrefix: "PLVR" },

  // ── Activewear ───────────────────────────────────────────────────
  { key: "sports_bra",     label: "Sports bra",          category: "activewear",  sizes: SZ_ALPHA_XS, styleCodePrefix: "SBRA" },
  { key: "active_tee",     label: "Active tee",          category: "activewear",  sizes: SZ_ALPHA,    styleCodePrefix: "ATEE" },
  { key: "cycling_shorts", label: "Cycling shorts",      category: "activewear",  sizes: SZ_ALPHA,    styleCodePrefix: "CYCS" },
  { key: "tracksuit",      label: "Tracksuit",           category: "activewear",  sizes: SZ_ALPHA,    styleCodePrefix: "TRKS" },

  // ── Inner & loungewear ───────────────────────────────────────────
  { key: "boxer",        label: "Boxer",                 category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "BOXR" },
  { key: "brief",        label: "Brief",                 category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "BREF" },
  { key: "camisole",     label: "Camisole",              category: "inner_loungewear", sizes: SZ_ALPHA_XS, styleCodePrefix: "CAMI" },
  { key: "pyjama_set",   label: "Pyjama set",           category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "PYJA" },
  { key: "nightwear",    label: "Nightwear",             category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "NGHT" },
  { key: "robe",         label: "Robe",                  category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "ROBE" },
  { key: "thermal_set",  label: "Thermal set",           category: "inner_loungewear", sizes: SZ_ALPHA,    styleCodePrefix: "THRM" },

  // ── Ethnicwear (India) ───────────────────────────────────────────
  { key: "kurta",        label: "Kurta",                 category: "ethnicwear",  sizes: SZ_WOVEN,    styleCodePrefix: "KRTA" },
  { key: "kurti",        label: "Kurti",                 category: "ethnicwear",  sizes: SZ_ALPHA_XS, styleCodePrefix: "KRTI" },
  { key: "salwar",       label: "Salwar",                category: "ethnicwear",  sizes: SZ_ALPHA,    styleCodePrefix: "SLWR" },
  { key: "churidar",     label: "Churidar",              category: "ethnicwear",  sizes: SZ_ALPHA,    styleCodePrefix: "CHDR" },
  { key: "saree_blouse", label: "Saree blouse",          category: "ethnicwear",  sizes: SZ_WOVEN,    styleCodePrefix: "SRBL" },
  { key: "lehenga",      label: "Lehenga",               category: "ethnicwear",  sizes: SZ_ALPHA_XS, styleCodePrefix: "LHGA" },
  { key: "sherwani",     label: "Sherwani",              category: "ethnicwear",  sizes: SZ_WOVEN,    styleCodePrefix: "SHRW" },
  { key: "nehru_jacket", label: "Nehru jacket",          category: "ethnicwear",  sizes: SZ_WOVEN,    styleCodePrefix: "NHRJ" },

  // ── Kidswear ─────────────────────────────────────────────────────
  { key: "kidswear",     label: "Kidswear (general)",    category: "kidswear",    sizes: SZ_KIDS,     styleCodePrefix: "KIDS" },
  { key: "kids_romper",  label: "Kids romper",           category: "kidswear",    sizes: SZ_KIDS,     styleCodePrefix: "KROM" },
  { key: "frock",        label: "Frock",                 category: "kidswear",    sizes: SZ_KIDS,     styleCodePrefix: "FRCK" },

  // ── Accessories ──────────────────────────────────────────────────
  { key: "cap",          label: "Cap / hat",             category: "accessories", sizes: SZ_ONESIZE,  styleCodePrefix: "CAP" },
  { key: "beanie",       label: "Beanie",                category: "accessories", sizes: SZ_ONESIZE,  styleCodePrefix: "BNIE" },
  { key: "scarf",        label: "Scarf / stole",         category: "accessories", sizes: SZ_ONESIZE,  styleCodePrefix: "SCRF" },
  { key: "socks",        label: "Socks",                 category: "accessories", sizes: SZ_SHOE,     styleCodePrefix: "SOCK" },
  { key: "apron",        label: "Apron",                 category: "accessories", sizes: SZ_ONESIZE,  styleCodePrefix: "APRN" },
  { key: "tote_bag",     label: "Tote bag",              category: "accessories", sizes: SZ_ONESIZE,  styleCodePrefix: "TOTE" },
];

const TEMPLATE_BY_KEY = new Map<string, GarmentTypeTemplate>(
  GARMENT_TYPE_TEMPLATES.map((t) => [t.key, t]),
);

/** Lookup a template by key, or `undefined` if the key isn't in the catalog. */
export function garmentTypeTemplate(key: string): GarmentTypeTemplate | undefined {
  return TEMPLATE_BY_KEY.get(key);
}

/** Whether `key` is one of the known garment types. */
export function isGarmentType(key: string): key is GarmentType {
  return TEMPLATE_BY_KEY.has(key);
}

